import { useEffect, useMemo, useState } from 'react'
import {
  FileText,
  Image as ImageIcon,
  Presentation,
  FileSpreadsheet,
  FileArchive,
  FileCode,
  FileQuestion,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import { useContentThumbnail } from '@/hooks/use-api'

interface Props {
  contentVersionId: string
  fileType?: string
  hasThumbnail: boolean
  title: string
  className?: string
  size?: 'card' | 'row' // card = wide cover (16:10); row = square 56px
}

// GRK-BRAND-6: file-type styling from the categorical palette — `tint` fills the
// cover, `solid` is the icon glyph (>= 3:1). The ext label uses Prussian text.
type IconMeta = { icon: typeof FileText; tint: string; solid: string; ext: string }
const fam = (family: keyof typeof CATEGORICAL) => ({
  tint: CATEGORICAL[family].tint,
  solid: CATEGORICAL[family].solid,
})
const ICON_MAP: Record<string, IconMeta> = {
  PDF: { icon: FileText, ...fam('raspberry'), ext: 'PDF' },
  PNG: { icon: ImageIcon, ...fam('navy'), ext: 'PNG' },
  JPG: { icon: ImageIcon, ...fam('navy'), ext: 'JPG' },
  JPEG: { icon: ImageIcon, ...fam('navy'), ext: 'JPG' },
  GIF: { icon: ImageIcon, ...fam('navy'), ext: 'GIF' },
  WORD: { icon: FileText, ...fam('steel'), ext: 'DOC' },
  WORD_X: { icon: FileText, ...fam('steel'), ext: 'DOCX' },
  EXCEL: { icon: FileSpreadsheet, ...fam('evergreen'), ext: 'XLS' },
  EXCEL_X: { icon: FileSpreadsheet, ...fam('evergreen'), ext: 'XLSX' },
  POWER_POINT: { icon: Presentation, ...fam('clay'), ext: 'PPT' },
  POWER_POINT_X: { icon: Presentation, ...fam('clay'), ext: 'PPTX' },
  HTML: { icon: FileCode, ...fam('teal'), ext: 'HTML' },
  TEXT: { icon: FileText, ...fam('slate'), ext: 'TXT' },
  ZIP: { icon: FileArchive, ...fam('slate'), ext: 'ZIP' },
  CSV: { icon: FileSpreadsheet, ...fam('evergreen'), ext: 'CSV' },
  SNOTE: { icon: FileText, ...fam('plum'), ext: 'NOTE' },
}

const FALLBACK: IconMeta = { icon: FileQuestion, ...fam('slate'), ext: 'FILE' }

export function RepositoryAssetThumbnail({
  contentVersionId,
  fileType,
  hasThumbnail,
  title,
  className,
  size = 'card',
}: Props) {
  const meta = useMemo(() => {
    if (!fileType) return FALLBACK
    return (
      ICON_MAP[fileType.toUpperCase()] || { ...FALLBACK, ext: fileType.slice(0, 4).toUpperCase() }
    )
  }, [fileType])

  // Only fetch when we know a thumbnail exists.
  const { data, isLoading } = useContentThumbnail(contentVersionId, hasThumbnail)
  const blob = data?.blob

  // Each component lifecycle owns its own object URL. The Blob stays cached
  // by react-query, so toggling views or remounting just builds a new URL
  // from the same bytes. Revoking on unmount cannot poison the cache.
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob) {
      setUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(blob)
    setUrl(objectUrl)
    return () => {
      try {
        URL.revokeObjectURL(objectUrl)
      } catch (_) {
        /* ignore */
      }
    }
  }, [blob])

  const aspect = size === 'row' ? 'h-14 w-14' : 'aspect-[16/10] w-full'
  const Icon = meta.icon

  if (hasThumbnail && url) {
    return (
      <div className={cn('overflow-hidden rounded-md border bg-muted', aspect, className)}>
        <img
          src={url}
          alt={`${title} thumbnail`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
    )
  }

  // Fallback cover (also rendered while signed URL is loading)
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-md border',
        aspect,
        className
      )}
      style={{ backgroundColor: meta.tint }}
      role="img"
      aria-label={`${title}${fileType ? ` — ${fileType} document` : ''}`}
    >
      <Icon
        className={cn('h-8 w-8', isLoading && hasThumbnail && 'animate-pulse')}
        style={{ color: meta.solid }}
        aria-hidden="true"
      />
      {size === 'card' && (
        <span
          className="mt-1 text-[10px] font-semibold tracking-wide"
          style={{ color: GRACKLE_COLORS.prussianBlue }}
        >
          {meta.ext}
        </span>
      )}
    </div>
  )
}
